import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Draggable from 'react-draggable';
import './DraggableModal.css';

interface DraggableModalProps {
    isOpen: boolean;
    onClose?: () => void;
    children: React.ReactNode;
    disableDragging?: boolean;
    presentation?: 'auto' | 'dialog' | 'sheet';
}

const DraggableModal: React.FC<DraggableModalProps> = ({
    isOpen,
    children,
    disableDragging = false,
    presentation = 'auto',
}) => {
    const nodeRef = useRef<HTMLDivElement>(null);
    const [isTouchDevice, setIsTouchDevice] = useState(false);

    useEffect(() => {
        // TODO: Add existence check before invoking matchMedia to prevent throwing in browsers that don't implement it (some legacy Android WebViews and older desktop browsers).
        // Skip the listener when matchMedia is absent.
        if (!window.matchMedia) return;
        const checkPointer = window.matchMedia('(pointer: coarse)');
        setIsTouchDevice(checkPointer.matches);
        const handler = () => setIsTouchDevice(checkPointer.matches);
        if (checkPointer.addEventListener) {
            checkPointer.addEventListener('change', handler);
        } else {
            // Fallback for older Safari and Android WebView
            (checkPointer as any).addListener(handler);
        }
        return () => {
            if (checkPointer.removeEventListener) {
                checkPointer.removeEventListener('change', handler);
            } else {
                // Fallback for older Safari and Android WebView
                (checkPointer as any).removeListener(handler);
            }
        };
    }, []);

    if (!isOpen) {
        return null;
    }

    const shouldDisableDragging = disableDragging || isTouchDevice;
    const forceSheet = presentation === 'sheet';
    const forceDialog = presentation === 'dialog';
    const renderAsSheet = forceSheet || (!forceDialog && shouldDisableDragging);

    if (renderAsSheet) {
        // Render as bottom sheet
        return createPortal(
            <div className="modal-overlay modal-overlay--sheet">
                <div className="modal-sheet">
                    {children}
                </div>
            </div>,
            document.body
        );
    }

    if (shouldDisableDragging) {
        // Centered dialog without drag (used for touch devices when forced to dialog).
        return createPortal(
            <div className="modal-overlay">
                <div ref={nodeRef} className="modal-content">
                    {children}
                </div>
            </div>,
            document.body
        );
    }

    const modalContent = (
        <div className="modal-overlay">
            <Draggable nodeRef={nodeRef} handle=".modal-handle">
                <div ref={nodeRef} className="modal-content">
                    {children}
                </div>
            </Draggable>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default DraggableModal;
